import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { toExternalUrl } from '@/lib/utils';
import useSWR from 'swr';
import { Search, Plus, Star } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger
} from '@/components/ui/tooltip';
import { DialogFooter } from '@/components/ui/dialog';
import { FormModal } from '@/components/shared';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import {
    addVideoSchema,
    AddVideoInput,
    addLinkSchema,
    AddLinkInput
} from '@/lib/validation';
import { Pagination } from '@/components/Pagination';
import { useUrlPagination } from '@/hooks/useUrlPagination';
import { useDebounceValue } from 'usehooks-ts';
import { toast } from 'sonner';
import {
    Facility,
    Library,
    Video,
    HelpfulLink,
    HelpfulLinkAndSort,
    ServerResponseMany,
    ServerResponseOne,
    Option,
    FeatureAccess,
    MAX_DOWNLOAD_ATTEMPTS
} from '@/types';
import { useAuth, canSwitchFacility, hasFeature } from '@/auth/useAuth';
import { FacilityVisibilitySheet } from '@/components/knowledge-center';
import { ManagedContent } from '@/components/knowledge-center/FacilityVisibilitySheet';
import {
    videoIsAvailable,
    getVideoErrorMessage,
    formatVideoDuration
} from '@/lib/formatters';
import API from '@/api/api';
import { decodeHtmlEntities } from '@/lib/decodeHtmlEntities';

interface CardHandlers {
    onToggleFeatured: (
        id: number,
        type: 'library' | 'video' | 'link',
        isFeatured: boolean
    ) => void;
    onToggleVisibility: (
        id: number,
        type: 'library' | 'video' | 'link',
        isVisible: boolean
    ) => void;
    onNavigate: (path: string) => void;
}

function VisibilityFooter({
    visible,
    visibleFacilityCount,
    facilityCount,
    onToggle,
    onManage
}: {
    visible: boolean;
    visibleFacilityCount?: number;
    facilityCount?: number;
    onToggle: () => void;
    onManage?: () => void;
}) {
    return (
        <div
            className="row-with-border mt-auto"
            onClick={(e) => e.stopPropagation()}
        >
            {onManage ? (
                <>
                    <span className="text-sm text-gray-700">
                        {(visibleFacilityCount ?? 0) === 0
                            ? 'Hidden everywhere'
                            : `Visible at ${visibleFacilityCount} / ${facilityCount}`}
                    </span>
                    <Button variant="outline" size="sm" onClick={onManage}>
                        Manage
                    </Button>
                </>
            ) : (
                <>
                    <label className="clickable-row">
                        <Switch
                            checked={visible}
                            onCheckedChange={onToggle}
                            className="data-[state=checked]:bg-brand"
                        />
                        <span className="text-gray-700">Visible</span>
                    </label>
                    <Badge
                        variant={visible ? 'default' : 'secondary'}
                        className={
                            visible
                                ? 'bg-green-100 text-green-800 hover:bg-green-100'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
                        }
                    >
                        {visible ? 'Visible' : 'Hidden'}
                    </Badge>
                </>
            )}
        </div>
    );
}

function LibraryCard({
    library,
    handlers,
    facilityCount,
    onManage
}: {
    library: Library;
    handlers: CardHandlers;
    facilityCount?: number;
    onManage?: (library: Library) => void;
}) {
    const title = decodeHtmlEntities(library.title);
    const description = decodeHtmlEntities(library.description ?? '');
    return (
        <div
            className="media-card group flex flex-col h-full"
            onClick={() =>
                handlers.onNavigate(`/viewer/libraries/${library.id}`)
            }
        >
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlers.onToggleFeatured(
                                    library.id,
                                    'library',
                                    !!library.is_featured
                                );
                            }}
                            className="btn-corner"
                        >
                            <Star
                                className={`size-4 ${library.is_featured ? 'text-brand-gold fill-brand-gold' : 'text-gray-300'}`}
                            />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="text-sm">
                            Featured content appears at the top for residents
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <div className="flex items-start gap-3 mb-3">
                <img
                    src={library.thumbnail_url ?? ''}
                    alt={title}
                    className="size-12 rounded shrink-0 border border-gray-200"
                />
                <div className="flex-1 min-w-0 pr-8">
                    <h3 className="card-title-link">{title}</h3>
                </div>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2 mb-3 flex-1">
                {description}
            </p>
            <VisibilityFooter
                visible={library.visibility_status}
                visibleFacilityCount={library.visible_facility_count}
                facilityCount={facilityCount}
                onToggle={() =>
                    handlers.onToggleVisibility(
                        library.id,
                        'library',
                        library.visibility_status
                    )
                }
                onManage={onManage && (() => onManage(library))}
            />
        </div>
    );
}

interface VideoCardProps {
    video: Video;
    handlers: CardHandlers;
    onRetry: (video: Video) => void;
    onViewStatus: (video: Video) => void;
    facilityCount?: number;
    onManage?: (video: Video) => void;
}

function VideoCard({
    video,
    handlers,
    onRetry,
    onViewStatus,
    facilityCount,
    onManage
}: VideoCardProps) {
    const available = videoIsAvailable(video);
    const title = decodeHtmlEntities(video.title);
    const channelTitle = decodeHtmlEntities(video.channel_title ?? '');
    const description = decodeHtmlEntities(video.description ?? '');
    return (
        <div
            className={`bg-white rounded-lg border ${!available ? 'border-red-300' : 'border-gray-200'} p-4 hover:shadow-lg hover:border-brand transition-all cursor-pointer group relative flex flex-col h-full`}
            onClick={() => {
                if (available)
                    handlers.onNavigate(`/viewer/videos/${video.id}`);
            }}
        >
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlers.onToggleFeatured(
                                    video.id,
                                    'video',
                                    !!video.is_featured
                                );
                            }}
                            className="btn-corner"
                        >
                            <Star
                                className={`size-4 ${video.is_featured ? 'text-brand-gold fill-brand-gold' : 'text-gray-300'}`}
                            />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="text-sm">
                            Featured content appears at the top for residents
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <div className="flex items-start gap-3 mb-3">
                <div className="relative shrink-0">
                    <img
                        src={`/api/photos/${video.external_id}.jpg`}
                        alt={title}
                        className="size-12 rounded object-cover border border-gray-200"
                    />
                    <div className="absolute -bottom-1 -right-1 bg-black/80 text-white text-[10px] px-1 py-0.5 rounded">
                        {formatVideoDuration(video.duration)}
                    </div>
                </div>
                <div className="flex-1 min-w-0 pr-8">
                    <h3 className="card-title-link">{title}</h3>
                    <p className="text-sm text-gray-500">{channelTitle}</p>
                </div>
            </div>
            {available ? (
                <p className="text-sm text-gray-600 line-clamp-2 mb-3 flex-1">
                    {description}
                </p>
            ) : (
                <div
                    className="mb-3 flex-1"
                    onClick={(e) => e.stopPropagation()}
                >
                    <p className="text-sm text-red-600 mb-2">
                        {getVideoErrorMessage(video) ??
                            'Video is processing...'}
                    </p>
                    {video.video_download_attempts.length <
                        MAX_DOWNLOAD_ATTEMPTS && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => onRetry(video)}
                        >
                            Try Again
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs ml-2"
                        onClick={() => onViewStatus(video)}
                    >
                        View Status
                    </Button>
                </div>
            )}
            <VisibilityFooter
                visible={video.visibility_status}
                visibleFacilityCount={video.visible_facility_count}
                facilityCount={facilityCount}
                onToggle={() =>
                    handlers.onToggleVisibility(
                        video.id,
                        'video',
                        video.visibility_status
                    )
                }
                onManage={onManage && (() => onManage(video))}
            />
        </div>
    );
}

function LinkCard({
    link,
    handlers,
    onLinkClick,
    facilityCount,
    onManage
}: {
    link: HelpfulLink;
    handlers: CardHandlers;
    onLinkClick: (link: HelpfulLink) => void;
    facilityCount?: number;
    onManage?: (link: HelpfulLink) => void;
}) {
    const title = decodeHtmlEntities(link.title);
    const description = decodeHtmlEntities(link.description ?? '');
    return (
        <div
            className="media-card group flex flex-col h-full"
            onClick={() => onLinkClick(link)}
        >
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handlers.onToggleFeatured(
                                    link.id,
                                    'link',
                                    !!link.is_featured
                                );
                            }}
                            className="btn-corner"
                        >
                            <Star
                                className={`size-4 ${link.is_featured ? 'text-brand-gold fill-brand-gold' : 'text-gray-300'}`}
                            />
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p className="text-sm">
                            Featured content appears at the top for residents
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <div className="pr-8 mb-2">
                <h3 className="card-title-link">{title}</h3>
            </div>
            <p className="text-sm text-brand mb-2 truncate">{link.url}</p>
            <p className="text-sm text-gray-600 line-clamp-2 mb-3 flex-1">
                {description}
            </p>
            <VisibilityFooter
                visible={link.visibility_status}
                visibleFacilityCount={link.visible_facility_count}
                facilityCount={facilityCount}
                onToggle={() =>
                    handlers.onToggleVisibility(
                        link.id,
                        'link',
                        link.visibility_status
                    )
                }
                onManage={onManage && (() => onManage(link))}
            />
        </div>
    );
}

export default function KnowledgeCenterManagement() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const canManageFacilities = !!user && canSwitchFacility(user);
    const canViewHelpfulLinks =
        !!user && hasFeature(user, FeatureAccess.HelpfulLinksAccess);
    const canViewVideos =
        !!user && hasFeature(user, FeatureAccess.UploadVideoAccess);
    const [manageContent, setManageContent] = useState<ManagedContent | null>(
        null
    );

    const [searchTerm, setSearchTerm] = useState('');
    const [searchQuery] = useDebounceValue(searchTerm, 500);
    const [visibilityFilter, setVisibilityFilter] = useState<string>('all');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [currentTab, setCurrentTab] = useState('libraries');
    const {
        page: currentPage,
        perPage: itemsPerPage,
        setPage: setCurrentPage,
        setPerPage
    } = useUrlPagination();

    const [showAddVideo, setShowAddVideo] = useState(false);
    const [showAddLink, setShowAddLink] = useState(false);

    const videoForm = useForm<AddVideoInput>({
        resolver: zodResolver(addVideoSchema),
        defaultValues: { url: '' }
    });
    const linkForm = useForm<AddLinkInput>({
        resolver: zodResolver(addLinkSchema),
        defaultValues: { title: '', url: '', description: '' }
    });

    const [polling, setPolling] = useState(false);
    const pollingRef = useRef(false);
    const recentToggles = useRef(new Set<string>());
    const [statusDialogOpen, setStatusDialogOpen] = useState(false);
    const [targetVideo, setTargetVideo] = useState<Video | null>(null);

    useEffect(() => {
        if (currentTab !== 'libraries') {
            setCategoryFilter('all');
        }
        setCurrentPage(1);
    }, [currentTab, setCurrentPage]);

    useEffect(() => {
        if (currentTab === 'links' && !canViewHelpfulLinks) {
            setCurrentTab('libraries');
        }
        if (currentTab === 'videos' && !canViewVideos) {
            setCurrentTab('libraries');
        }
    }, [currentTab, canViewHelpfulLinks, canViewVideos]);

    useEffect(() => {
        if (showAddVideo) {
            videoForm.reset({ url: '' });
        }
    }, [showAddVideo, videoForm]);

    useEffect(() => {
        if (showAddLink) {
            linkForm.reset({ title: '', url: '', description: '' });
        }
    }, [showAddLink, linkForm]);

    const visibilityParam = useMemo(() => {
        if (
            visibilityFilter === 'featured' ||
            visibilityFilter === 'not-featured'
        )
            return '&visibility=all';
        if (visibilityFilter === 'all') return '&visibility=all';
        return `&visibility=${visibilityFilter}`;
    }, [visibilityFilter]);

    const categoryParam = useMemo(() => {
        if (categoryFilter === 'all') return '';
        return `&tags=${categoryFilter}`;
    }, [categoryFilter]);

    const { data: tagsData } = useSWR<ServerResponseMany<Option>>('/api/tags');
    const categories = tagsData?.data ?? [];

    const { data: facilitiesData } = useSWR<ServerResponseMany<Facility>>(
        canManageFacilities ? '/api/facilities?page=1&per_page=1' : null
    );
    const facilityCount = facilitiesData?.meta?.total ?? 0;

    const { data: libData, mutate: mutateLibs } = useSWR<
        ServerResponseMany<Library>
    >(
        `/api/libraries?page=1&per_page=500&order_by=title&order=asc${visibilityParam}&search=${searchQuery}${categoryParam}`
    );

    const { data: vidData, mutate: mutateVids } = useSWR<
        ServerResponseMany<Video>
    >(
        canViewVideos
            ? `/api/videos?page=1&per_page=500&order_by=title&order=asc${visibilityParam}&search=${searchQuery}`
            : null
    );

    const { data: linkData, mutate: mutateLinks } = useSWR<
        ServerResponseOne<HelpfulLinkAndSort>
    >(
        canViewHelpfulLinks
            ? `/api/helpful-links?search=${searchQuery}&page=1&per_page=500&order_by=title&order=asc`
            : null
    );

    const libraries = useMemo(() => {
        const items = libData?.data ?? [];
        if (visibilityFilter === 'featured') {
            return items.filter((lib) => lib.is_featured);
        }
        if (visibilityFilter === 'not-featured') {
            return items.filter((lib) => !lib.is_featured);
        }
        return items;
    }, [libData?.data, visibilityFilter]);

    const libTotal = useMemo(() => {
        if (
            visibilityFilter === 'featured' ||
            visibilityFilter === 'not-featured'
        )
            return libraries.length;
        return libData?.meta?.total ?? 0;
    }, [libData?.meta?.total, visibilityFilter, libraries.length]);

    const videos = useMemo(() => {
        const items = vidData?.data ?? [];
        if (visibilityFilter === 'featured') {
            return items.filter((vid) => vid.is_featured);
        }
        if (visibilityFilter === 'not-featured') {
            return items.filter((vid) => !vid.is_featured);
        }
        return items;
    }, [vidData?.data, visibilityFilter]);

    const vidTotal = useMemo(() => {
        if (
            visibilityFilter === 'featured' ||
            visibilityFilter === 'not-featured'
        )
            return videos.length;
        return vidData?.meta?.total ?? 0;
    }, [vidData?.meta?.total, visibilityFilter, videos.length]);

    const helpfulLinks = useMemo(() => {
        const items = linkData?.data?.helpful_links ?? [];
        if (visibilityFilter !== 'all' && visibilityFilter !== 'not-featured') {
            const vis =
                visibilityFilter === 'visible'
                    ? true
                    : visibilityFilter === 'hidden'
                      ? false
                      : null;
            if (vis !== null) {
                return items.filter((link) => link.visibility_status === vis);
            }
            if (visibilityFilter === 'featured') {
                return items.filter((link) => link.is_featured);
            }
        }
        if (visibilityFilter === 'not-featured') {
            return items.filter((link) => !link.is_featured);
        }
        return items;
    }, [linkData?.data?.helpful_links, visibilityFilter]);

    const linkTotal = useMemo(() => {
        if (visibilityFilter !== 'all') return helpfulLinks.length;
        return linkData?.data?.meta?.total ?? 0;
    }, [linkData?.data?.meta?.total, visibilityFilter, helpfulLinks.length]);

    const paginatedLibraries = libraries.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const paginatedVideos = videos.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const paginatedLinks = helpfulLinks.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handleItemsPerPageChange = setPerPage;

    const pollVideos = (delay: number) => {
        if (!pollingRef.current) return;
        void mutateVids().then(() => {
            if (delay > 10000) {
                pollingRef.current = false;
                setPolling(false);
                return;
            }
            setTimeout(() => pollVideos(delay * 2), delay);
        });
    };

    const guardToggle = (key: string): boolean => {
        if (recentToggles.current.has(key)) return false;
        recentToggles.current.add(key);
        setTimeout(() => recentToggles.current.delete(key), 1000);
        return true;
    };

    const handleToggleFeatured = async (
        id: number,
        type: 'library' | 'video' | 'link',
        isFeatured: boolean
    ) => {
        if (!guardToggle(`featured-${type}-${id}`)) return;
        const endpoints: Record<string, string> = {
            library: `libraries/${id}/favorite`,
            video: `videos/${id}/feature`,
            link: `helpful-links/favorite/${id}`
        };
        const typeLabel: Record<string, string> = {
            library: 'Library',
            video: 'Video',
            link: 'Helpful link'
        };
        const actionString = isFeatured ? 'unfeatured' : 'featured';
        const resp = await API.put<null, object>(endpoints[type], {});
        if (resp.success) {
            toast.success(`${typeLabel[type]} ${actionString}`);
            if (type === 'library') void mutateLibs();
            else if (type === 'video') void mutateVids();
            else void mutateLinks();
        } else {
            toast.error(`${typeLabel[type]} ${actionString}`);
        }
    };

    const handleToggleVisibility = async (
        id: number,
        type: 'library' | 'video' | 'link',
        isVisible: boolean
    ) => {
        if (!guardToggle(`visibility-${type}-${id}`)) return;
        const endpoints: Record<string, string> = {
            library: `libraries/${id}/toggle`,
            video: `videos/${id}/visibility`,
            link: `helpful-links/toggle/${id}`
        };
        const typeLabel: Record<string, string> = {
            library: 'Library',
            video: 'Video',
            link: 'Helpful link'
        };
        const actionString = isVisible ? 'is now hidden' : 'is now visible';
        const resp = await API.put<null, object>(endpoints[type], {});
        if (resp.success) {
            toast.success(`${typeLabel[type]} ${actionString}`);
            if (type === 'library') void mutateLibs();
            else if (type === 'video') void mutateVids();
            else void mutateLinks();
        } else {
            toast.error(`${typeLabel[type]} ${actionString}`);
        }
    };

    const handleAddVideo = async (formData: AddVideoInput) => {
        const resp = await API.post<null, object>('videos', {
            video_urls: [formData.url]
        });
        if (resp.success) {
            toast.success('Video added successfully');
            setShowAddVideo(false);
            videoForm.reset({ url: '' });
            pollingRef.current = true;
            setPolling(true);
            setTimeout(() => pollVideos(1000), 1000);
            void mutateVids();
        } else {
            toast.error(resp.message ?? 'Failed to add video');
        }
    };

    const handleAddLink = async (formData: AddLinkInput) => {
        const resp = await API.put<null, object>('helpful-links', formData);
        if (resp.success) {
            toast.success('Link added successfully');
            setShowAddLink(false);
            linkForm.reset({ title: '', url: '', description: '' });
            void mutateLinks();
        } else {
            toast.error(resp.message ?? 'Failed to add link');
        }
    };

    const handleRetryVideo = async (video: Video) => {
        if (polling) {
            toast.error('Please wait before retrying again');
            return;
        }
        const resp = await API.put<null, object>(
            `videos/${video.id}/retry`,
            {}
        );
        if (resp.success) {
            toast.success('Video retry started');
            pollingRef.current = true;
            setPolling(true);
            setTimeout(() => pollVideos(1000), 1000);
        } else {
            toast.error('Error retrying video');
        }
    };

    const handleLinkClick = async (link: HelpfulLink) => {
        const resp = await API.put<{ url: string }, object>(
            `helpful-links/activity/${link.id}`,
            {}
        );
        if (resp.success && resp.data) {
            window.open(
                toExternalUrl((resp.data as { url: string }).url ?? link.url),
                '_blank'
            );
        } else {
            window.open(toExternalUrl(link.url), '_blank');
        }
    };

    const getStatusText = () => {
        if (!targetVideo) return '';
        return targetVideo.video_download_attempts.length >=
            MAX_DOWNLOAD_ATTEMPTS
            ? 'This video has reached the maximum download attempts. Please remove and try again.'
            : `Download is processing: ${getVideoErrorMessage(targetVideo) ?? ''}\nThe video download will be retried every 3 hours.`;
    };

    const cardHandlers: CardHandlers = {
        onToggleFeatured: (id, type, isFeatured) =>
            void handleToggleFeatured(id, type, isFeatured),
        onToggleVisibility: (id, type, isVisible) =>
            void handleToggleVisibility(id, type, isVisible),
        onNavigate: (path) => navigate(path)
    };

    return (
        <div className="max-w-7xl mx-auto px-6 py-8">
            <div className="mb-8">
                <h1 className="text-brand-dark mb-2">Knowledge Center</h1>
                <p className="text-gray-600">
                    Manage libraries, videos, and helpful links for residents.
                </p>
            </div>

            <div className="card-block p-4 mb-6">
                <div className="flex gap-4">
                    <div className="relative flex-1">
                        <Search className="input-icon-left size-5" />
                        <Input
                            placeholder="Search across all content..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                            }}
                            className="pl-10"
                        />
                    </div>
                    <Select
                        value={visibilityFilter}
                        onValueChange={(v) => {
                            setVisibilityFilter(v);
                            setCurrentPage(1);
                        }}
                    >
                        <SelectTrigger className="w-45">
                            <SelectValue placeholder="Filter by" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Content</SelectItem>
                            <SelectItem value="visible">
                                Visible Only
                            </SelectItem>
                            <SelectItem value="hidden">Hidden Only</SelectItem>
                            <SelectItem value="featured">
                                Featured Only
                            </SelectItem>
                            <SelectItem value="not-featured">
                                Not Featured
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <Tabs
                value={currentTab}
                onValueChange={setCurrentTab}
                className="space-y-6"
            >
                <div className="flex items-center justify-between">
                    <TabsList className="bg-white border border-gray-200 p-1 h-auto gap-1">
                        <TabsTrigger
                            value="libraries"
                            className="tab-trigger-brand"
                        >
                            Libraries ({libTotal})
                        </TabsTrigger>
                        {canViewVideos && (
                            <TabsTrigger
                                value="videos"
                                className="tab-trigger-brand"
                            >
                                Videos ({vidTotal})
                            </TabsTrigger>
                        )}
                        {canViewHelpfulLinks && (
                            <TabsTrigger
                                value="links"
                                className="tab-trigger-brand"
                            >
                                Helpful Links ({linkTotal})
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {currentTab === 'libraries' && (
                        <Select
                            value={categoryFilter}
                            onValueChange={(v) => {
                                setCategoryFilter(v);
                                setCurrentPage(1);
                            }}
                        >
                            <SelectTrigger className="w-70">
                                <SelectValue placeholder="Filter by category" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">
                                    All Categories
                                </SelectItem>
                                {categories.map((cat) => (
                                    <SelectItem
                                        key={String(cat.key)}
                                        value={String(cat.key)}
                                    >
                                        {String(cat.value)}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
                    {currentTab === 'videos' && canViewVideos && (
                        <Button
                            className="bg-brand hover:bg-brand-dark text-white"
                            onClick={() => setShowAddVideo(true)}
                        >
                            <Plus className="size-4 mr-2" />
                            Add Video
                        </Button>
                    )}
                    {currentTab === 'links' && canViewHelpfulLinks && (
                        <Button
                            className="bg-brand hover:bg-brand-dark text-white"
                            onClick={() => setShowAddLink(true)}
                        >
                            <Plus className="size-4 mr-2" />
                            Add Link
                        </Button>
                    )}
                </div>

                <TabsContent value="libraries" className="space-y-4">
                    {libraries.length === 0 ? (
                        <div className="empty-state">
                            <p className="text-gray-500">No libraries found.</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 gap-6">
                                {paginatedLibraries.map((library) => (
                                    <LibraryCard
                                        key={library.id}
                                        library={library}
                                        handlers={cardHandlers}
                                        facilityCount={facilityCount}
                                        onManage={
                                            canManageFacilities
                                                ? (l) =>
                                                      setManageContent({
                                                          title: l.title,
                                                          endpoint: `libraries/${l.id}/facilities`
                                                      })
                                                : undefined
                                        }
                                    />
                                ))}
                            </div>
                            <Pagination
                                currentPage={currentPage}
                                totalItems={libraries.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </TabsContent>

                <TabsContent
                    value="videos"
                    className="space-y-4"
                    hidden={!canViewVideos}
                >
                    {videos.length === 0 ? (
                        <div className="empty-state">
                            <p className="text-gray-500">No videos found.</p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 gap-6">
                                {paginatedVideos.map((video) => (
                                    <VideoCard
                                        key={video.id}
                                        video={video}
                                        handlers={cardHandlers}
                                        facilityCount={facilityCount}
                                        onManage={
                                            canManageFacilities
                                                ? (v) =>
                                                      setManageContent({
                                                          title: v.title,
                                                          endpoint: `videos/${v.id}/facilities`
                                                      })
                                                : undefined
                                        }
                                        onRetry={(v) =>
                                            void handleRetryVideo(v)
                                        }
                                        onViewStatus={(v) => {
                                            setTargetVideo(v);
                                            setStatusDialogOpen(true);
                                        }}
                                    />
                                ))}
                            </div>
                            <Pagination
                                currentPage={currentPage}
                                totalItems={videos.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </TabsContent>

                <TabsContent
                    value="links"
                    className="space-y-4"
                    hidden={!canViewHelpfulLinks}
                >
                    {helpfulLinks.length === 0 ? (
                        <div className="empty-state">
                            <p className="text-gray-500">
                                No helpful links found.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-3 gap-6">
                                {paginatedLinks.map((link) => (
                                    <LinkCard
                                        key={link.id}
                                        link={link}
                                        handlers={cardHandlers}
                                        facilityCount={facilityCount}
                                        onManage={
                                            canManageFacilities
                                                ? (l) =>
                                                      setManageContent({
                                                          title: l.title,
                                                          endpoint: `helpful-links/facilities/${l.id}`
                                                      })
                                                : undefined
                                        }
                                        onLinkClick={(l) =>
                                            void handleLinkClick(l)
                                        }
                                    />
                                ))}
                            </div>
                            <Pagination
                                currentPage={currentPage}
                                totalItems={helpfulLinks.length}
                                itemsPerPage={itemsPerPage}
                                onPageChange={setCurrentPage}
                                onItemsPerPageChange={handleItemsPerPageChange}
                            />
                        </>
                    )}
                </TabsContent>
            </Tabs>

            <FormModal
                open={showAddVideo}
                onOpenChange={setShowAddVideo}
                title="Add YouTube Video"
                description="Enter a YouTube URL to add a video to the Knowledge Center."
                titleClassName="text-foreground"
            >
                <Form {...videoForm}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            void videoForm.handleSubmit(handleAddVideo)(e);
                        }}
                    >
                        <div className="space-y-4 pt-4">
                            <FormField
                                control={videoForm.control}
                                name="url"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>YouTube URL</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="https://www.youtube.com/watch?v=..."
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowAddVideo(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="brand"
                                    disabled={videoForm.formState.isSubmitting}
                                >
                                    Add Video
                                </Button>
                            </div>
                        </div>
                    </form>
                </Form>
            </FormModal>

            <FormModal
                open={showAddLink}
                onOpenChange={setShowAddLink}
                title="Add Helpful Link"
                description="Add a whitelisted website link for residents to access."
                titleClassName="text-foreground"
            >
                <Form {...linkForm}>
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            void linkForm.handleSubmit(handleAddLink)(e);
                        }}
                    >
                        <div className="space-y-4 pt-4">
                            <FormField
                                control={linkForm.control}
                                name="title"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Title *</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="Enter link title"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={linkForm.control}
                                name="url"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>URL *</FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder="https://example.com"
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={linkForm.control}
                                name="description"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Description *</FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder="Enter a brief description of this resource"
                                                rows={3}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <div className="flex justify-end gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setShowAddLink(false)}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    variant="brand"
                                    disabled={linkForm.formState.isSubmitting}
                                >
                                    Add Link
                                </Button>
                            </div>
                        </div>
                    </form>
                </Form>
            </FormModal>

            <FormModal
                open={statusDialogOpen}
                onOpenChange={setStatusDialogOpen}
                title="Video Status"
                titleClassName="text-foreground"
            >
                <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {getStatusText()}
                </p>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => {
                            setStatusDialogOpen(false);
                            setTargetVideo(null);
                        }}
                    >
                        Close
                    </Button>
                </DialogFooter>
            </FormModal>

            <FacilityVisibilitySheet
                content={manageContent}
                onClose={() => setManageContent(null)}
                onChanged={() => {
                    void mutateLibs();
                    void mutateVids();
                    void mutateLinks();
                }}
            />
        </div>
    );
}
